import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { activityApi, taskApi } from '../services/api';
import { Task, LeadActivity } from '../types';
import { fmtLongDay, fmtTime, istDay, istToday, pct, shiftDay } from '../lib';
import NumberStrip, { StripItem } from '../components/NumberStrip';
import { Empty, Failed, Loading } from '../components/WorkBits';
import { purposeOf, leadName } from './queueBits';

/** The visit board — the one number the whole project is judged by (§0).
 *
 *  A visit exists in two estates and this screen is where they meet: what is
 *  PLANNED is an open `site_visit` task, what HAPPENED is a `kind=visit`
 *  activity. Grouped by IST day, newest day first among past days.
 */

type Row =
  | { kind: 'planned'; at: string; leadId: string; name: string; detail: string; task: Task }
  | { kind: 'conducted'; at: string; leadId: string; name: string; detail: string };

const VisitsPage: React.FC = () => {
  const navigate = useNavigate();
  const today = istToday();
  const [from, setFrom] = useState(shiftDay(today, -7));
  const [to, setTo] = useState(shiftDay(today, 14));
  const [planned, setPlanned] = useState<Task[] | null>(null);
  const [conducted, setConducted] = useState<LeadActivity[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setPlanned(null);
    setConducted(null);
    setError(null);
    try {
      const [p, c] = await Promise.all([
        taskApi.list({ status: 'open', type: 'site_visit' }),
        activityApi.list({
          kind: 'visit',
          // IST day bounds expressed in UTC — the API compares against honest UTC.
          occurred_from: new Date(`${from}T00:00:00+05:30`).toISOString(),
          occurred_to: new Date(`${shiftDay(to, 1)}T00:00:00+05:30`).toISOString(),
          limit: 1000,
        }),
      ]);
      setPlanned(p.tasks);
      setConducted(c.activity);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="px-7 py-6"><Failed what="the visit board" error={error} /></div>;
  if (!planned || !conducted) return <div className="px-7 py-6"><Loading what="visits" /></div>;

  const inWindow = (day: string) => day >= from && day <= to;

  const rows: Row[] = [
    ...planned
      .filter((t) => inWindow(istDay(t.due_at)))
      .map((t): Row => ({
        kind: 'planned',
        at: t.due_at,
        leadId: t.lead_id,
        name: leadName(t),
        detail: purposeOf(t) || 'Site visit',
        task: t,
      })),
    ...conducted.map((a): Row => ({
      kind: 'conducted',
      at: a.occurred_at,
      leadId: a.lead_id,
      name: a.lead?.name?.trim() || a.lead?.mobile || 'Unknown lead',
      detail: a.summary || a.payload?.notes || 'Visit conducted',
    })),
  ].sort((a, b) => a.at.localeCompare(b.at));

  const days = Array.from(new Set(rows.map((r) => istDay(r.at)))).sort();

  const scheduled = rows.filter((r) => r.kind === 'planned').length;
  const done = rows.filter((r) => r.kind === 'conducted').length;
  const reconfirmed = planned.filter((t) => inWindow(istDay(t.due_at)) && t.payload?.reconfirmed_at).length;

  const strip: StripItem[] = [
    { v: scheduled, k: 'scheduled', cls: 'gold' },
    { v: done, k: 'conducted', cls: 'visited', p: pct(done, scheduled + done) },
    { v: reconfirmed, k: 'reconfirmed', p: pct(reconfirmed, scheduled) },
  ];

  const dayLabel = (day: string) =>
    day === today ? 'Today' : day === shiftDay(today, 1) ? 'Tomorrow' : fmtLongDay(`${day}T06:00:00Z`);

  return (
    <div className="w-full px-7 pt-6 pb-16">
      <div className="page-head">
        <div>
          <h1 className="page-title">Visits</h1>
          <p className="page-sub">Planned visits are open tasks; conducted visits are activities · IST days</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="date" className="inp" value={from}
            onChange={(e) => e.target.value && setFrom(e.target.value > to ? to : e.target.value)}
          />
          <span className="text-graphite font-bold">→</span>
          <input
            type="date" className="inp" value={to}
            onChange={(e) => e.target.value && setTo(e.target.value < from ? from : e.target.value)}
          />
        </div>
      </div>

      <div className="card"><NumberStrip items={strip} /></div>

      {rows.length === 0 && <Empty>No visits in this window.</Empty>}

      {days.map((day) => (
        <div key={day}>
          <div className="groupline">{dayLabel(day)}</div>
          <div className="space-y-3">
            {rows.filter((r) => istDay(r.at) === day).map((r, i) => {
              const conductedRow = r.kind === 'conducted';
              // Unconfirmed and today is the state that must catch the eye.
              const unconfirmed = r.kind === 'planned' && !r.task.payload?.reconfirmed_at;
              const edge = conductedRow ? 'border-l-forest'
                : unconfirmed && day <= today ? 'border-l-brick' : 'border-l-gold';
              return (
                <div
                  key={`${r.kind}-${r.leadId}-${i}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/leads/${r.leadId}`)}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/leads/${r.leadId}`)}
                  className={`card card-click border-l-4 ${edge}`}
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="num font-display text-base font-semibold w-[74px]">{fmtTime(r.at)}</span>
                    <span className="font-semibold text-[15px]">{r.name}</span>
                    {conductedRow
                      ? <span className="chip chip-forest">conducted</span>
                      : unconfirmed
                        ? <span className="chip chip-brick">unconfirmed</span>
                        : <span className="chip chip-gold">confirmed</span>}
                    <span className="text-graphite text-[12.5px] truncate">{r.detail}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default VisitsPage;
